import {
  BrowserProvider,
  hexlify,
  Signer,
  parseEther,
  TransactionRequest,
  TransactionResponse,
  TypedDataDomain,
  TypedDataField,
  JsonRpcSigner,
  JsonRpcApiProvider,
} from 'ethers';

import config from '#/config';

import { throwOnDifferentThanCurrentChainId } from '../chains';

import { initializeWalletConnect } from './walletConnectV2';

import type EthereumProvider from '@walletconnect/ethereum-provider';

const makeWeb3Signer = (etherumProvider: EthereumProvider) => {
  console.log('[walletConnecytV2Signer] RPC Chain', etherumProvider.chainId);
  const chainName = Object.values(config.chains).find(
    chain => chain.chainId === etherumProvider.chainId
  )?.chainNetwork;
  const web3Provider = new BrowserProvider(etherumProvider, {
    chainId: etherumProvider.chainId,
    name: chainName || `Chain ${etherumProvider.chainId}`,
  });
  return web3Provider.getSigner();
};

/**
 * Note: that currently Trezor does not expose method to check internal nonce, so we use transaction count.
 *
 * To prevent "Nonce too low" on multiple pending transactions, like deposit (approve & deposit call),
 * we need to track if nonce which we are using is really highest nonce.
 */
let highestNonceWalletAddress = '';
let highestNonceSubmitted = 0;

export class WalletConnectSigner extends JsonRpcSigner {
  public address: string;
  private useWalletRpc: boolean;

  constructor(
    provider: JsonRpcApiProvider,
    address: string,
    useWalletRpc: boolean
  ) {
    super(provider, address);
    this.address = address;
    this.useWalletRpc = useWalletRpc;
  }

  connect(provider: JsonRpcApiProvider): Signer {
    return new WalletConnectSigner(provider, this.address, this.useWalletRpc);
  }

  _legacySignMessage(_message: string | Uint8Array): Promise<string> {
    throw new Error('Unimplemented walletConnect _legacySignMessage');
  }

  unlock(_password: string): Promise<boolean> {
    throw new Error('Unimplemented walletConnect unlock');
  }

  sendUncheckedTransaction(_tx: TransactionRequest): Promise<string> {
    throw new Error('Unimplemented sendUncheckedTransaction');
  }

  async getPublicKey(/* path: string */): Promise<string> {
    const provider = await initializeWalletConnect();
    const account = provider.accounts[0];
    return account;
  }

  async getAddress() {
    return this.address;
  }

  setAddress(address: string) {
    this.address = address;
  }

  async getChainId(): Promise<number> {
    const provider = await initializeWalletConnect();
    if (!provider.chainId) {
      return -1;
    }
    return provider.chainId;
  }

  async signMessage(message: string): Promise<string> {
    const provider = await initializeWalletConnect();
    const account = provider.accounts[0];

    console.log('[WalletConnect] Sign message using wallet', account);
    const signer = await makeWeb3Signer(provider);
    const signature = signer.signMessage(message);
    // TO-DO: There might be "Session currently disconnected" when signing
    return signature;
  }

  async signTypedData(
    domain: TypedDataDomain,
    types: Record<string, TypedDataField[]>,
    value: Record<string, any>
  ): Promise<string> {
    const provider = await initializeWalletConnect();
    const account = provider.accounts[0];

    console.log('[WalletConnect] Sign typed data using wallet', account);
    const signer = await makeWeb3Signer(provider);
    const signature = signer.signTypedData(domain, types, value);
    return signature;
  }

  private async prepareTransaction(
    transactionRaw: TransactionRequest
  ): Promise<TransactionRequest> {
    console.log('[WalletConnect] Sign transaction', transactionRaw);
    const transaction = { ...transactionRaw };
    // delete transaction.chainId;
    // delete transaction.nonce;
    // delete transaction.gasLimit;
    if (transaction.from !== undefined) {
      const from = transaction.from;
      if (`${from}`.toLowerCase() !== this.address.toLowerCase()) {
        throw new Error('Wallet address missmatch error');
      }
    } else {
      transaction.from = hexlify(this.address);
    }
    if (transaction.chainId !== undefined) {
      throwOnDifferentThanCurrentChainId(Number(transaction.chainId));
      // REMOVED_HEXLIFY: transaction.chainId = ethers.hexlify(transaction.chainId);
    } else {
      // We can set chain here manually, but that cause error in MetaMask
      // REMOVED_HEXLIFY: transaction.chainId = utils.hexlify(chainConfig.chainId);
      // transaction.chainId = chainConfig.chainId;
      // transaction.chainId = hexlify(chainConfig.chainId.toString()); // hexlify('0x138DE');
    }
    if (transaction.value !== undefined) {
      // TODO: walletConnect: hexlify not needed ?
      // transaction.value = hexlify(transaction.value);
      // transaction.value = transaction.value;
    } else {
      // // REMOVED_HEXLIFY hexlify not needed ?
      // transaction.value = hexlify(parseEther('0.0'));
      transaction.value = parseEther('0.0');
    }
    // if (transaction.gasPrice !== undefined) {
    //   // REMOVED_HEXLIFY hexlify not needed ?
    //   transaction.gasPrice = hexlify(transaction.gasPrice);
    // }
    // if (transaction.gasLimit !== undefined) {
    //   // REMOVED_HEXLIFY hexlify not needed ?
    //   transaction.gasLimit = hexlify(transaction.gasLimit);
    // }
    // if (transaction.nonce !== undefined) {}

    const transactionsCount = await this.provider?.getTransactionCount(
      this.address,
      'latest'
    );

    if (transactionsCount === undefined) {
      throw new Error('Internal error - provider not found');
    }

    if (highestNonceWalletAddress !== this.address) {
      // Reset nonce on wallet change
      highestNonceSubmitted = 0;
      highestNonceWalletAddress = this.address;
    }
    const nonce = Math.max(
      transactionsCount,
      highestNonceSubmitted > 0 ? highestNonceSubmitted + 1 : 0,
      typeof transaction.nonce === 'number' ? transaction.nonce : 0
    );
    // We increase nonce only after successfull submit
    highestNonceSubmitted = Math.max(nonce - 1, 0);

    // // REMOVED_HEXLIFY should be number instead ?
    // transaction.nonce = hexlify(nonce);
    transaction.nonce = nonce;

    return transaction;
  }

  // Note: Check if Metamask support https://github.com/MetaMask/metamask-extension/issues/7711
  async signTransaction(transactionRaw: TransactionRequest): Promise<string> {
    // throw new Error('Unimplemented custom RPC sign');
    const provider = await initializeWalletConnect();
    const preparedTransaction = await this.prepareTransaction(transactionRaw);
    const signer = await makeWeb3Signer(provider);
    const signature: string = await signer.signTransaction(preparedTransaction);
    console.log('[walletConnect] Signature', signature);
    if (signature && signature.length > 0 && !signature.startsWith('0x')) {
      // iOS version of TrustWallet does not return "0x" form
      return `0x${signature}`;
    }
    return signature;
  }

  // Alternative way when it's submitted inside wallet (without our RPC)
  private async signAndDispatchTransaction(
    transaction: TransactionRequest
  ): Promise<TransactionResponse> {
    const provider = await initializeWalletConnect();
    throwOnDifferentThanCurrentChainId(provider.chainId);
    console.log('[WalletConnect] Sign transaction prepared', transaction);
    const webProvider = await makeWeb3Signer(provider);
    const response = await webProvider.sendTransaction(transaction);
    console.log('[WalletConnect] Tx hash', response.hash);
    return response;
  }

  async sendTransaction(
    transaction: TransactionRequest
  ): Promise<TransactionResponse> {
    // if (this.useWalletRpc) {
    const preparedTransaction = await this.prepareTransaction(transaction);
    // We cannot enforce chain here as some wallets, eg. MetaMask decline transaction
    // if (!preparedTransaction.chainId) {
    //   throw new Error('Unknown chain id ');
    // }
    if (!preparedTransaction.gasLimit) {
      throw new Error('Unknown gas limit');
    }
    const txResponse = await this.signAndDispatchTransaction(
      preparedTransaction
    );
    // const { hash } = txResponse;
    highestNonceSubmitted += 1;

    // return new TransactionResponse(
    //   {
    //     hash,

    //   },
    //   this.provider
    // );

    // txResponse.nonce = highestNonceSubmitted;

    return txResponse;

    // TODO: walletConnectV2 not needed code below ?
    // return {
    //   ...txResponse,
    //   hash,
    //   // timestamp: new Date().getTime(),
    //   from: transaction.from as string,
    //   wait: async () => {
    //     await waitAsync(1000);
    //     let receipt: ethers.TransactionReceipt | undefined | null;
    //     let tries = 0;
    //     while (!receipt) {
    //       if (tries > 0) {
    //         await waitAsync(4000);
    //       }
    //       // 40 seconds timeout
    //       if (tries > 10) {
    //         throw new Error(`We were unable to find your transaction ${hash}.`);
    //       }
    //       console.log('Retrying to get receipt', hash);
    //       // TO-DO: walletConnect - might need to use enforced provider based on Chain (eg. rewards)
    //       receipt = await this.provider?.getTransactionReceipt(hash);
    //       // receipt = await getAnonymousWalletProvider().getTransactionReceipt(

    //       tries += 1;
    //     }
    //     return receipt;
    //   },
    //   confirmations: () => Promise.resolve(0),
    //   nonce: highestNonceSubmitted,
    //   gasLimit: BigInt(preparedTransaction.gasLimit),
    //   data: preparedTransaction.data?.toString() || '',
    //   value: BigInt(preparedTransaction.value || 0),
    //   chainId: BigInt(preparedTransaction.chainId),
    // };

    // } else {
    // Dispatch using RPC provider (Alchemy)
    // const response = await super.sendTransaction(transaction);
    // previousHighestNonceSubmitted += 1;
    // return response;
    // }
  }
}
